/**
 * Shop filters. Desktop updates the grid when a choice settles.
 * The phone drawer keeps choices pending until Show products.
 */

import { syncFavouriteButtons } from './favourites.js';
import { abortShopInfinite, bindShopInfinite } from './shop-infinite.js';

const DESKTOP = window.matchMedia('(min-width: 1024px)');
const SETTLE_MS = 250;

/** @type {AbortController|null} */
let gridRequest = null;

/** @type {AbortController|null} */
let countRequest = null;

/** @type {number} */
let applyTimer = 0;

/** @type {number} */
let countTimer = 0;

function desktop() {
	return DESKTOP.matches;
}

/**
 * Pixel length of a root custom property such as 1rem or 16px.
 *
 * @param {string} name
 */
function rootLengthPx(name) {
	const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
	if (raw.endsWith('rem')) {
		const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
		return parseFloat(raw) * root;
	}
	if (raw.endsWith('px')) {
		return parseFloat(raw);
	}
	return 16;
}

/**
 * Keep the desktop filter sidebar under the sticky header as its own scroller.
 */
function bindFilterSidebarOffset() {
	const sidebar = document.querySelector('.dc-shop-filters--sidebar');
	const header = document.querySelector('.dc-header');
	if (!(sidebar instanceof HTMLElement) || !(header instanceof HTMLElement)) {
		return;
	}

	const apply = () => {
		const headerHeight = Math.ceil(header.getBoundingClientRect().height);
		const gap = rootLengthPx('--dc-space-4');
		document.documentElement.style.setProperty(
			'--dc-shop-filters-offset',
			`${Math.ceil(headerHeight + gap)}px`
		);
	};

	apply();
	if (typeof ResizeObserver === 'undefined') {
		return;
	}
	const observer = new ResizeObserver(apply);
	observer.observe(header);
}

/**
 * @param {HTMLFormElement|null} form
 */
function formIsDrawer(form) {
	return Boolean(form && form.closest('#dc-filters-drawer'));
}

/**
 * @param {HTMLElement|null} main
 * @param {boolean} busy
 */
function setShopBusy(main, busy) {
	if (!main) {
		return;
	}
	main.classList.toggle('is-loading', busy);
	main.setAttribute('aria-busy', busy ? 'true' : 'false');
}

/**
 * @param {number|null} count
 */
function setShowLabel(count) {
	const button = document.querySelector('[data-filters-show]');
	if (!(button instanceof HTMLButtonElement)) {
		return;
	}
	if (count === null) {
		button.textContent = 'Show products';
		return;
	}
	const total = Number(count) || 0;
	if (total < 1) {
		button.textContent = 'No matching products';
		return;
	}
	button.textContent = total === 1 ? 'Show 1 product' : `Show ${total.toLocaleString()} products`;
}

/**
 * @param {boolean} busy
 */
function setShowBusy(busy) {
	const button = document.querySelector('[data-filters-show]');
	if (!(button instanceof HTMLButtonElement)) {
		return;
	}
	button.disabled = busy;
	button.classList.toggle('is-loading', busy);
	button.setAttribute('aria-busy', busy ? 'true' : 'false');
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @returns {string[]}
 */
function checkedValues(form, name) {
	return [...form.querySelectorAll('input[type="checkbox"]')]
		.filter((input) => input instanceof HTMLInputElement && input.name === name && input.checked)
		.map((input) => input.value);
}

/**
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function buildUrl(form) {
	const landing = form.dataset.landingGoal || '';
	const shop = form.dataset.shopUrl || form.action;
	const goals = checkedValues(form, 'dc_goal[]');
	let action = form.dataset.action || form.action;
	let goalParams = goals;

	if (landing) {
		if (goals.length === 1 && goals[0] === landing) {
			goalParams = [];
		} else {
			action = shop;
		}
	}

	const url = new URL(action, window.location.origin);
	const params = new URLSearchParams();
	const stock = form.querySelector('[data-stock-toggle]');
	if (stock instanceof HTMLInputElement && !stock.checked) {
		params.set('dc_stock', 'any');
	}
	const delivery = form.querySelector('[data-delivery-toggle]');
	if (delivery instanceof HTMLInputElement && delivery.checked) {
		params.set('dc_delivery', '1');
	}
	const organic = form.querySelector('[data-organic-toggle]');
	if (organic instanceof HTMLInputElement && organic.checked) {
		params.set('dc_organic', '1');
	}

	const append = (key, values) => {
		values.forEach((value) => {
			if (value) {
				params.append(key, value);
			}
		});
	};
	append('dc_dept[]', checkedValues(form, 'dc_dept[]'));
	append('dc_goal[]', goalParams);
	append('dc_brand[]', checkedValues(form, 'dc_brand[]'));
	append('dc_dietary[]', checkedValues(form, 'dc_dietary[]'));
	append('dc_form[]', checkedValues(form, 'dc_form[]'));

	const boundsMin = Number(form.dataset.boundsMin);
	const boundsMax = Number(form.dataset.boundsMax);
	const min = Number(form.querySelector('[data-price-value="min"]')?.value);
	const max = Number(form.querySelector('[data-price-value="max"]')?.value);
	if (Number.isFinite(min) && min > boundsMin) {
		params.set('min_price', String(min));
	}
	if (Number.isFinite(max) && max < boundsMax) {
		params.set('max_price', String(max));
	}

	const orderby = form.querySelector('input[name="orderby"]');
	if (orderby instanceof HTMLInputElement && orderby.value) {
		params.set('orderby', orderby.value);
	}
	const search = form.querySelector('input[name="s"]');
	if (search instanceof HTMLInputElement && search.value) {
		params.set('s', search.value);
	}

	url.search = params.toString();
	return url.toString();
}

/**
 * @param {HTMLFormElement} form
 */
function updatePriceVisual(form) {
	const min = form.querySelector('[data-price-range="min"]');
	const max = form.querySelector('[data-price-range="max"]');
	if (!(min instanceof HTMLInputElement) || !(max instanceof HTMLInputElement)) {
		return;
	}

	const start = Number(min.min);
	const end = Number(min.max);
	const span = Math.max(1, end - start);
	const left = ((Number(min.value) - start) / span) * 100;
	const right = ((Number(max.value) - start) / span) * 100;
	const fill = form.querySelector('[data-price-fill]');
	if (fill instanceof HTMLElement) {
		fill.style.left = `${left}%`;
		fill.style.width = `${Math.max(0, right - left)}%`;
	}

	const symbol = form.dataset.currency || '$';
	form.querySelectorAll('[data-price-edit]').forEach((button) => {
		if (!(button instanceof HTMLButtonElement) || button.dataset.editing === '1') {
			return;
		}
		const which = button.dataset.priceEdit === 'max' ? max : min;
		button.textContent = symbol + which.value;
	});

	const minValue = form.querySelector('[data-price-value="min"]');
	const maxValue = form.querySelector('[data-price-value="max"]');
	if (minValue instanceof HTMLInputElement) {
		minValue.value = min.value;
	}
	if (maxValue instanceof HTMLInputElement) {
		maxValue.value = max.value;
	}

	min.style.zIndex = Number(min.value) >= Number(max.value) ? '5' : '4';
	max.style.zIndex = '3';
}

/**
 * @param {HTMLInputElement} input
 */
function clampRange(input) {
	const form = input.form;
	if (!form) {
		return;
	}
	const min = form.querySelector('[data-price-range="min"]');
	const max = form.querySelector('[data-price-range="max"]');
	if (!(min instanceof HTMLInputElement) || !(max instanceof HTMLInputElement)) {
		return;
	}
	if (input === min && Number(min.value) > Number(max.value)) {
		min.value = max.value;
	}
	if (input === max && Number(max.value) < Number(min.value)) {
		max.value = min.value;
	}
	updatePriceVisual(form);
}

/**
 * @param {HTMLInputElement} input
 */
function filterBrands(input) {
	const section = input.closest('[data-section="brand"]');
	const query = input.value.trim().toLowerCase();
	section?.querySelectorAll('[data-brand-option]').forEach((item) => {
		if (!(item instanceof HTMLElement)) {
			return;
		}
		const label = item.dataset.label || '';
		const checked = item.querySelector('input') instanceof HTMLInputElement && item.querySelector('input').checked;
		item.hidden = query !== '' && !checked && !label.includes(query);
	});
}

/**
 * @param {HTMLFormElement} target
 * @param {HTMLFormElement} source
 */
function syncFormValues(target, source) {
	target.querySelectorAll('input[type="checkbox"]').forEach((input) => {
		if (!(input instanceof HTMLInputElement)) {
			return;
		}
		const match = [...source.querySelectorAll('input[type="checkbox"]')].find(
			(candidate) => candidate instanceof HTMLInputElement && candidate.name === input.name && candidate.value === input.value
		);
		if (match instanceof HTMLInputElement) {
			input.checked = match.checked;
		}
	});

	['min', 'max'].forEach((which) => {
		const from = source.querySelector(`[data-price-range="${which}"]`);
		const to = target.querySelector(`[data-price-range="${which}"]`);
		if (from instanceof HTMLInputElement && to instanceof HTMLInputElement) {
			to.min = from.min;
			to.max = from.max;
			to.value = from.value;
		}
	});

	['action', 'shopUrl', 'landingGoal', 'resetUrl', 'boundsMin', 'boundsMax', 'facetSignature', 'currency'].forEach((key) => {
		if (source.dataset[key] !== undefined) {
			target.dataset[key] = source.dataset[key];
		}
	});
	if (source.getAttribute('action')) {
		target.setAttribute('action', source.getAttribute('action') || '');
	}

	const orderbyFrom = source.querySelector('input[name="orderby"]');
	const orderbyTo = target.querySelector('input[name="orderby"]');
	if (orderbyFrom instanceof HTMLInputElement && orderbyTo instanceof HTMLInputElement) {
		orderbyTo.value = orderbyFrom.value;
	}
	const searchFrom = source.querySelector('input[name="s"]');
	const searchTo = target.querySelector('input[name="s"]');
	if (searchFrom instanceof HTMLInputElement && searchTo instanceof HTMLInputElement) {
		searchTo.value = searchFrom.value;
	}

	updatePriceVisual(target);
}

/**
 * @param {string} href
 */
function syncResetLinks(href) {
	if (!href) {
		return;
	}
	document.querySelectorAll('[data-filters-reset]').forEach((link) => {
		if (link instanceof HTMLAnchorElement) {
			link.href = href;
		}
	});
}

/**
 * @param {HTMLFormElement} nextForm
 */
function replaceForms(nextForm) {
	document.querySelectorAll('.dc-shop-filters-form').forEach((form) => {
		if (!(form instanceof HTMLFormElement)) {
			return;
		}
		const scroller = form.closest('.dc-shop-filters--sidebar, .dc-filters-drawer__body');
		const scroll = scroller instanceof HTMLElement ? scroller.scrollTop : 0;
		const open = new Set(
			[...form.querySelectorAll('details[data-section][open]')].map((item) => item.getAttribute('data-section') || '')
		);
		const searchValue = form.querySelector('[data-brand-search]') instanceof HTMLInputElement
			? form.querySelector('[data-brand-search]').value
			: '';
		const clone = /** @type {HTMLFormElement} */ (nextForm.cloneNode(true));
		form.replaceWith(clone);
		clone.querySelectorAll('details[data-section]').forEach((item) => {
			if (item instanceof HTMLDetailsElement) {
				item.open = open.has(item.dataset.section || '');
			}
		});
		const search = clone.querySelector('[data-brand-search]');
		if (search instanceof HTMLInputElement) {
			search.value = searchValue;
			filterBrands(search);
		}
		updatePriceVisual(clone);
		if (scroller instanceof HTMLElement) {
			scroller.scrollTop = scroll;
		}
	});
	syncResetLinks(nextForm.querySelector('[data-filters-reset]')?.href || nextForm.dataset.resetUrl || '');
}

/**
 * @param {HTMLFormElement} nextForm
 */
function syncLiveForms(nextForm) {
	document.querySelectorAll('.dc-shop-filters-form').forEach((form) => {
		if (form instanceof HTMLFormElement) {
			syncFormValues(form, nextForm);
		}
	});
	syncResetLinks(nextForm.querySelector('[data-filters-reset]')?.href || nextForm.dataset.resetUrl || '');
}

/**
 * @param {ParentNode} doc
 */
function adoptResponseForms(doc) {
	const nextForm = doc.querySelector('.dc-shop-filters--sidebar .dc-shop-filters-form');
	const current = document.querySelector('.dc-shop-filters-form');
	if (!(nextForm instanceof HTMLFormElement) || !(current instanceof HTMLFormElement)) {
		return;
	}
	if (nextForm.dataset.facetSignature !== current.dataset.facetSignature) {
		replaceForms(nextForm);
		return;
	}
	syncLiveForms(nextForm);
}

/**
 * @param {string} href
 * @param {{ push?: boolean, scroll?: boolean }} [options]
 */
async function refreshShop(href, options = {}) {
	const push = options.push !== false;
	const url = new URL(href, window.location.origin);
	url.searchParams.delete('dc_count');
	url.searchParams.delete('paged');
	url.searchParams.delete('product-page');
	url.pathname = url.pathname.replace(/\/page\/\d+\/?$/, '/');
	const main = document.querySelector('.dc-shop-main');

	window.clearTimeout(countTimer);
	countRequest?.abort();
	gridRequest?.abort();
	abortShopInfinite();
	const controller = new AbortController();
	gridRequest = controller;
	setShopBusy(main instanceof HTMLElement ? main : null, true);

	try {
		const response = await fetch(url, {
			signal: controller.signal,
			credentials: 'same-origin',
			cache: 'no-store',
			headers: { 'X-Requested-With': 'fetch' },
		});

		if (!response.ok) {
			throw new Error('Shop filter request failed');
		}

		const html = await response.text();
		const doc = new DOMParser().parseFromString(html, 'text/html');
		const nextMain = doc.querySelector('.dc-shop-main');
		const currentMain = document.querySelector('.dc-shop-main');

		if (!(nextMain instanceof HTMLElement) || !(currentMain instanceof HTMLElement)) {
			throw new Error('Shop results missing');
		}

		currentMain.replaceWith(nextMain);
		adoptResponseForms(doc);
		syncFavouriteButtons();
		bindShopInfinite();
		setShopBusy(nextMain, false);
		setShowLabel(Number(nextMain.dataset.resultCount || '0'));

		if (push) {
			history.pushState({ dcShopFilters: true }, '', url);
		}
		if (options.scroll) {
			nextMain.scrollIntoView({ block: 'start' });
		}
		return true;
	} catch (error) {
		if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
			return false;
		}
		window.location.assign(url);
		return false;
	} finally {
		if (gridRequest === controller) {
			gridRequest = null;
			const current = document.querySelector('.dc-shop-main');
			if (current instanceof HTMLElement && current.getAttribute('aria-busy') === 'true') {
				setShopBusy(current, false);
			}
		}
	}
}

/**
 * @param {string} href
 */
async function refreshCount(href) {
	const url = new URL(href, window.location.origin);
	url.searchParams.set('dc_count', '1');
	url.searchParams.delete('paged');
	countRequest?.abort();
	const controller = new AbortController();
	countRequest = controller;
	setShowBusy(true);

	try {
		const response = await fetch(url, {
			signal: controller.signal,
			credentials: 'same-origin',
			cache: 'no-store',
			headers: {
				'X-Requested-With': 'fetch',
				Accept: 'application/json',
			},
		});
		if (!response.ok) {
			throw new Error('Count failed');
		}
		const data = await response.json();
		setShowLabel(Number(data.count));
	} catch (error) {
		if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
			return;
		}
		setShowLabel(null);
	} finally {
		if (countRequest === controller) {
			countRequest = null;
			setShowBusy(false);
		}
	}
}

/**
 * @param {HTMLFormElement} form
 */
function schedule(form) {
	if (formIsDrawer(form) && !desktop()) {
		window.clearTimeout(countTimer);
		countTimer = window.setTimeout(() => {
			refreshCount(buildUrl(form));
		}, SETTLE_MS);
		return;
	}
	if (formIsDrawer(form)) {
		return;
	}
	window.clearTimeout(applyTimer);
	applyTimer = window.setTimeout(() => {
		refreshShop(buildUrl(form));
	}, SETTLE_MS);
}

/**
 * @param {HTMLButtonElement} button
 */
function editPrice(button) {
	const form = button.closest('form');
	if (!(form instanceof HTMLFormElement) || button.dataset.editing === '1') {
		return;
	}
	const which = button.dataset.priceEdit === 'max' ? 'max' : 'min';
	const range = form.querySelector(`[data-price-range="${which}"]`);
	if (!(range instanceof HTMLInputElement)) {
		return;
	}

	const input = document.createElement('input');
	input.type = 'number';
	input.inputMode = 'numeric';
	input.min = range.min;
	input.max = range.max;
	input.step = '1';
	input.value = range.value;
	input.className = 'dc-price-slider__entry';
	input.setAttribute('aria-label', which === 'min' ? 'Minimum price' : 'Maximum price');
	button.dataset.editing = '1';
	button.textContent = '';
	button.append(input);
	input.focus();
	input.select();

	let settled = false;
	const finish = (commit) => {
		if (settled) {
			return;
		}
		settled = true;
		if (commit) {
			let value = Math.round(Number(input.value));
			if (!Number.isFinite(value)) {
				value = Number(range.value);
			}
			value = Math.min(Number(range.max), Math.max(Number(range.min), value));
			range.value = String(value);
			clampRange(range);
			schedule(form);
		}
		button.dataset.editing = '0';
		updatePriceVisual(form);
	};

	input.addEventListener('keydown', (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			input.blur();
		}
		if (event.key === 'Escape') {
			event.preventDefault();
			finish(false);
		}
	});
	input.addEventListener('blur', () => finish(true));
}

function closeSortMenu() {
	document.querySelectorAll('[data-dc-shop-sort-toggle]').forEach((toggle) => {
		toggle.setAttribute('aria-expanded', 'false');
	});
	document.querySelectorAll('[data-dc-shop-sort-menu]').forEach((menu) => {
		if (menu instanceof HTMLElement) {
			menu.hidden = true;
		}
	});
}

/**
 * @param {string} orderby
 * @returns {string}
 */
function sortUrl(orderby) {
	const url = new URL(window.location.href);
	url.searchParams.set('orderby', orderby);
	url.searchParams.delete('paged');
	url.searchParams.delete('product-page');
	url.searchParams.delete('dc_count');
	url.pathname = url.pathname.replace(/\/page\/\d+\/?$/, '/');
	return url.toString();
}

/**
 * @param {string} orderby
 */
function applyCatalogSort(orderby) {
	if (!orderby) {
		return;
	}
	document.querySelectorAll('.dc-shop-filters-form input[name="orderby"]').forEach((input) => {
		if (input instanceof HTMLInputElement) {
			input.value = orderby;
		}
	});
	refreshShop(sortUrl(orderby));
}

/**
 * @param {HTMLElement} drawer
 * @param {Element|null} trigger
 */
function openDrawer(drawer, trigger) {
	const source = document.querySelector('.dc-shop-filters--sidebar .dc-shop-filters-form');
	const target = drawer.querySelector('.dc-shop-filters-form');
	if (source instanceof HTMLFormElement && target instanceof HTMLFormElement) {
		syncFormValues(target, source);
		const search = target.querySelector('[data-brand-search]');
		if (search instanceof HTMLInputElement) {
			search.value = '';
			filterBrands(search);
		}
	}
	const count = Number(document.querySelector('.dc-shop-main')?.dataset.resultCount || '0');
	setShowLabel(count);
	drawer.hidden = false;
	drawer.classList.add('is-open');
	document.body.classList.add('no-scroll');
	drawer.querySelector('[data-filters-close]')?.focus();
	drawer._trigger = trigger;
}

/**
 * @param {HTMLElement} drawer
 */
function closeDrawer(drawer) {
	window.clearTimeout(countTimer);
	countRequest?.abort();
	countRequest = null;
	setShowBusy(false);
	const source = document.querySelector('.dc-shop-filters--sidebar .dc-shop-filters-form');
	const target = drawer.querySelector('.dc-shop-filters-form');
	if (source instanceof HTMLFormElement && target instanceof HTMLFormElement) {
		syncFormValues(target, source);
	}
	drawer.hidden = true;
	drawer.classList.remove('is-open');
	document.body.classList.remove('no-scroll');
	if (drawer._trigger instanceof HTMLElement) {
		drawer._trigger.focus();
	}
}

function trapFocus(drawer) {
	drawer.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			closeDrawer(drawer);
		}
	});
}

export function initFilters() {
	bindFilterSidebarOffset();

	document.querySelectorAll('.dc-shop-filters-form').forEach((form) => {
		if (form instanceof HTMLFormElement) {
			updatePriceVisual(form);
		}
	});

	document.addEventListener('change', (event) => {
		const input = event.target;
		if (!(input instanceof HTMLInputElement) || !input.closest('.dc-shop-filters-form')) {
			return;
		}
		if (input.matches('[data-price-range]')) {
			clampRange(input);
			if (input.form) {
				schedule(input.form);
			}
			return;
		}
		if (!(input.form instanceof HTMLFormElement)) {
			return;
		}
		schedule(input.form);
	});

	document.addEventListener('input', (event) => {
		const input = event.target;
		if (input instanceof HTMLInputElement && input.matches('[data-price-range]')) {
			clampRange(input);
		}
		if (input instanceof HTMLInputElement && input.matches('[data-brand-search]')) {
			filterBrands(input);
		}
	});

	document.addEventListener('keydown', (event) => {
		const input = event.target;
		if (event.key === 'Enter' && input instanceof HTMLInputElement && input.matches('[data-brand-search]')) {
			event.preventDefault();
		}
	});

	document.addEventListener('click', (event) => {
		const target = event.target;
		if (!(target instanceof Element)) {
			return;
		}

		const edit = target.closest('[data-price-edit]');
		if (edit instanceof HTMLButtonElement && !edit.querySelector('input')) {
			editPrice(edit);
			return;
		}

		const reset = target.closest('[data-filters-reset]');
		if (reset instanceof HTMLAnchorElement) {
			event.preventDefault();
			const drawer = document.getElementById('dc-filters-drawer');
			refreshShop(reset.href).then((ok) => {
				if (ok && drawer) {
					closeDrawer(drawer);
				}
			});
			return;
		}

		const filterLink = target.closest('[data-dc-filter-link]');
		if (filterLink instanceof HTMLAnchorElement) {
			event.preventDefault();
			refreshShop(filterLink.href);
			return;
		}

	});

	document.addEventListener('submit', (event) => {
		const form = event.target;
		if (!(form instanceof HTMLFormElement) || !form.classList.contains('dc-shop-filters-form')) {
			return;
		}
		event.preventDefault();
		if (formIsDrawer(form) && !desktop()) {
			return;
		}
		refreshShop(buildUrl(form));
	});

	document.addEventListener('change', (event) => {
		const select = event.target;
		if (!(select instanceof HTMLSelectElement) || !select.matches('[data-dc-shop-sort-select]')) {
			return;
		}
		applyCatalogSort(select.value);
	});

	document.addEventListener('click', (event) => {
		const target = event.target;
		if (!(target instanceof Element)) {
			return;
		}

		const toggle = target.closest('[data-dc-shop-sort-toggle]');
		if (toggle instanceof HTMLButtonElement) {
			const menu = document.getElementById(toggle.getAttribute('aria-controls') || '');
			const open = toggle.getAttribute('aria-expanded') === 'true';
			closeSortMenu();
			if (!open && menu) {
				toggle.setAttribute('aria-expanded', 'true');
				menu.hidden = false;
			}
			return;
		}

		const option = target.closest('[data-dc-shop-sort-option]');
		if (option instanceof HTMLButtonElement) {
			closeSortMenu();
			applyCatalogSort(option.getAttribute('data-orderby') || '');
			return;
		}

		if (!target.closest('[data-dc-shop-sort]')) {
			closeSortMenu();
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			closeSortMenu();
		}
	});

	window.addEventListener('popstate', () => {
		if (!document.querySelector('.dc-shop-main')) {
			return;
		}
		refreshShop(window.location.href, { push: false });
	});

	const drawer = document.getElementById('dc-filters-drawer');
	if (!drawer) {
		return;
	}
	trapFocus(drawer);

	document.addEventListener('click', (event) => {
		const target = event.target;
		if (!(target instanceof Element)) {
			return;
		}
		const openBtn = target.closest('[data-filters-open]');
		if (openBtn) {
			openDrawer(drawer, openBtn);
			return;
		}
		if (target.closest('[data-filters-close]')) {
			closeDrawer(drawer);
			return;
		}
		if (target.closest('[data-filters-show]')) {
			const form = drawer.querySelector('.dc-shop-filters-form');
			if (form instanceof HTMLFormElement) {
				refreshShop(buildUrl(form), { scroll: true }).then((ok) => {
					if (ok) {
						closeDrawer(drawer);
					}
				});
			}
		}
	});
}
